import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Add, ChevronLeft, ChevronRight, Close, Save } from '@mui/icons-material'
import { Box, Button, ButtonBase, Card, CardContent, Grid, IconButton, Link, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { borderRadiusDefault } from '../../../themes/theme'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useMatchScreenshotConceptsMutation, useSaveAnalysisScreenshotsMutation, useUnmatchScreenshotConceptsMutation } from '../../api/analysis-screenshots'
import { AddFeaturesDialog, AddFeaturesSelection } from '../../components/AddFeaturesDialog/AddFeaturesDialog'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog/UnsavedChangesDialog'
import { ConceptRow, FeatureRow, ScreenshotFeatureList } from '../../features/analysis-screenshots/components/ScreenshotFeatureList/ScreenshotFeatureList'
import { ScreenshotsHeader } from '../../features/analysis-screenshots/components/ScreenshotsHeader/ScreenshotsHeader'
import { Notice, WriteNotice } from '../../features/analysis-screenshots/components/WriteNotice/WriteNotice'
import {
  addFeatures,
  getConceptMatchGroups,
  getConceptUnmatchGroup,
  getThumbnailUrl,
  parseIndices,
  removeFeature,
  replaceItems,
  sameScreenshotLists,
  toStoredFeatureRows,
  writeLastViewedIndex,
} from '../../features/analysis-screenshots/helpers/screenshotHelpers'
import { getAnalysisScreenshotsPath } from '../../features/analysis-screenshots/helpers/screenshotPaths'
import {
  getConceptsReadBackProblem,
  getScreenshotsReadBackProblems,
  isScreenshotsChangedError,
  Message,
  toPositions,
} from '../../features/analysis-screenshots/helpers/writeMessages'
import { useAnalysisScreenshotsData } from '../../features/analysis-screenshots/hooks/useAnalysisScreenshotsData'
import { AnalysisScreenshotItem, AnalysisScreenshotView, ScreenshotFeature } from '../../features/analysis-screenshots/types/AnalysisScreenshots'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type RemoveRequest = { kind: 'feature'; row: FeatureRow } | { kind: 'concept'; row: ConceptRow }
// `fromUrl`: back/forward already changed the URL, and the page still shows the screenshots it had.
type PendingTarget = { ids: string[] | null; fromUrl: boolean }

const featureKey = ({ legacyId, weightId }: ScreenshotFeature) => `${legacyId}:${weightId}`

export const AnalysisScreenshotsEditPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysis-screenshots-edit'))
  const { analysisId = '' } = useParams<{ analysisId: string }>()
  return <AnalysisScreenshotsEdit key={analysisId} analysisId={analysisId} />
}

const AnalysisScreenshotsEdit: FC<{ analysisId: string }> = ({ analysisId }) => {
  const { t } = useTranslation()
  const { notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const data = useAnalysisScreenshotsData(analysisId)
  const [saveScreenshots, saveState] = useSaveAnalysisScreenshotsMutation()
  const [matchConcepts, matchState] = useMatchScreenshotConceptsMutation()
  const [unmatchConcepts, unmatchState] = useUnmatchScreenshotConceptsMutation()
  const [pendingFeatures, setPendingFeatures] = useState<ScreenshotFeature[]>([])
  const [pendingConceptIds, setPendingConceptIds] = useState<string[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [removeRequest, setRemoveRequest] = useState<ConfirmDialogData<RemoveRequest>>()
  const [pendingTarget, setPendingTarget] = useState<PendingTarget>()
  const [hoveredId, setHoveredId] = useState<string>()
  const [notice, setNotice] = useState<Notice>()
  // The URL names the screenshots by position, as legacy. The page holds their ids, so a list read again with another
  // order still edits the same screenshots; `shownParam` is the URL value they were taken from or written to.
  const [targetIds, setTargetIds] = useState<string[] | null>()
  const [shownParam, setShownParam] = useState<string | null>()

  const param = searchParams.get('indices')
  const isReady = !data.isLoading && !data.error
  const viewsById = useMemo(() => new Map(data.views.map((view) => [view.item.screenshotId, view])), [data.views])
  const views = useMemo(() => (targetIds ?? []).flatMap((id) => viewsById.get(id) ?? []), [targetIds, viewsById])
  const editedItems = useMemo(() => views.map(({ item }) => item), [views])
  const isDirty = pendingFeatures.length > 0 || pendingConceptIds.length > 0
  const { guard } = useEditPageLeave(isDirty)
  const isBusy = saveState.isLoading || matchState.isLoading || unmatchState.isLoading || data.isRefreshing
  const single = views.length === 1
  const positions = toPositions(data.items.map(({ screenshotId }) => screenshotId))

  const idsAt = useCallback(
    (value: string | null) => parseIndices(value, data.items.length)?.map((index) => data.items[index].screenshotId) ?? null,
    [data.items]
  )
  const toParam = (ids: string[]) => ids.flatMap((id) => viewsById.get(id)?.index ?? []).join(',')

  // The guard lets query changes pass, so back/forward to other screenshots is held here.
  useEffect(() => {
    if (!isReady || param === shownParam) return
    setShownParam(param)
    if (targetIds !== undefined && isDirty) setPendingTarget({ ids: idsAt(param), fromUrl: true })
    else setTargetIds(idsAt(param))
  }, [isReady, param, shownParam, targetIds, isDirty, idsAt])

  const stored = useMemo(
    () =>
      toStoredFeatureRows(
        views,
        views.find((view) => view.item.screenshotId === hoveredId)
      ),
    [views, hoveredId]
  )
  const featureRows: FeatureRow[] = [...stored.features, ...pendingFeatures.map((feature) => ({ feature, pending: true }))]
  const conceptRows: ConceptRow[] = [
    ...stored.concepts,
    ...pendingConceptIds.map((id) => ({
      concept: data.conceptsById.get(id) ?? { id, name: id, featureTagId: null },
      pending: true,
    })),
  ]
  const onEvery = <T,>(values: (view: AnalysisScreenshotView) => T[]) =>
    views.length ? values(views[0]).filter((value) => views.every((view) => values(view).includes(value))) : []
  const addedFeatureLegacyIds = [...onEvery((view) => view.features.map(({ legacyId }) => legacyId)), ...pendingFeatures.map(({ legacyId }) => legacyId)]
  const addedConceptIds = [...onEvery((view) => view.item.conceptIds), ...pendingConceptIds]

  const showMessages = (severity: Notice['severity'], messages: Message[]) => setNotice({ severity, messages })
  const saveFailure = (error: unknown): Message => ({
    key: isScreenshotsChangedError(error) ? 'internal-analysts:analysis_screenshots_changed' : 'internal-analysts:analysis_screenshots_save_failed',
  })

  const writeItems = async (changed: AnalysisScreenshotItem[]) => {
    const body = replaceItems(data.items, changed)
    if (sameScreenshotLists(body, data.items)) return []
    const { readBack } = await saveScreenshots({ analysisId, loaded: data.items, body }).unwrap()
    return getScreenshotsReadBackProblems(readBack, positions)
  }

  // Features first, then concepts, as legacy: a failed list save leaves the concepts unmatched and pending. What the
  // read-back doesn't show stays pending; saving again only sends what the stored screenshots still lack.
  const save = async () => {
    const messages: Message[] = []
    try {
      const problems = await writeItems(editedItems.map((item) => addFeatures(item, pendingFeatures)))
      messages.push(...problems)
      if (!problems.length) setPendingFeatures([])
    } catch (error) {
      showMessages('error', [saveFailure(error)])
      return false
    }
    const groups = getConceptMatchGroups(editedItems, pendingConceptIds)
    let conceptProblem: Message | undefined
    if (groups.length) {
      try {
        const { readBack } = await matchConcepts({ analysisId, groups }).unwrap()
        conceptProblem = getConceptsReadBackProblem(readBack, positions, true)
        if (conceptProblem) messages.push(conceptProblem)
      } catch {
        showMessages('error', [...messages, { key: 'internal-analysts:analysis_screenshots_match_failed' }])
        return false
      }
    }
    if (!conceptProblem) setPendingConceptIds([])
    if (messages.length) {
      showMessages('error', [
        { key: 'internal-analysts:analysis_screenshots_not_stored' },
        ...messages,
        { key: 'internal-analysts:analysis_screenshots_kept_pending' },
      ])
      return false
    }
    setNotice(undefined)
    notifySuccess(t('internal-analysts:analysis_screenshots_saved'))
    return true
  }

  const discard = () => {
    setPendingFeatures([])
    setPendingConceptIds([])
  }

  const removeStoredFeature = async ({ feature }: FeatureRow) => {
    const changed = editedItems
      .filter((item) => item.features[String(feature.legacyId)] === feature.weightId)
      .map((item) => removeFeature(item, feature.legacyId))
    try {
      const problems = await writeItems(changed)
      if (problems.length) showMessages('error', [{ key: 'internal-analysts:analysis_screenshots_not_stored' }, ...problems])
      else notifySuccess(t('internal-analysts:analysis_screenshots_feature_removed'))
    } catch (error) {
      showMessages('error', [saveFailure(error)])
    }
  }

  const removeStoredConcept = async ({ concept }: ConceptRow) => {
    const group = getConceptUnmatchGroup(editedItems, concept.id)
    if (!group) return
    try {
      const { readBack } = await unmatchConcepts({ analysisId, group }).unwrap()
      const problem = getConceptsReadBackProblem(readBack, positions, false)
      if (problem) showMessages('error', [problem])
      else notifySuccess(t('internal-analysts:analysis_screenshots_concept_removed'))
    } catch {
      showMessages('error', [{ key: 'internal-analysts:analysis_screenshots_unmatch_failed' }])
    }
  }

  const askRemove = (request: RemoveRequest) => {
    if (request.row.pending) {
      if (request.kind === 'feature') setPendingFeatures((features) => features.filter((feature) => featureKey(feature) !== featureKey(request.row.feature)))
      else setPendingConceptIds((ids) => ids.filter((id) => id !== request.row.concept.id))
      return
    }
    const name = request.kind === 'feature' ? `${request.row.feature.name}: ${request.row.feature.choice}` : request.row.concept.name
    const count = request.row.count ?? 1
    setRemoveRequest({
      title: t(
        request.kind === 'feature'
          ? 'internal-analysts:analysis_screenshots_remove_feature_title'
          : 'internal-analysts:analysis_screenshots_remove_concept_title'
      ),
      confirmText: t(
        request.kind === 'feature'
          ? 'internal-analysts:analysis_screenshots_remove_feature_confirm'
          : 'internal-analysts:analysis_screenshots_remove_concept_confirm',
        { name, count }
      ),
      actionText: t('internal-analysts:analysis_screenshots_remove'),
      destructiveAction: true,
      data: request,
    })
  }

  const handleAdd = ({ features, conceptIds }: AddFeaturesSelection) => {
    setIsDialogOpen(false)
    setPendingFeatures((current) => [...current, ...features.filter((feature) => !current.some(({ legacyId }) => legacyId === feature.legacyId))])
    setPendingConceptIds((current) => [...current, ...conceptIds.filter((id) => !current.includes(id))])
  }

  const showTargets = (ids: string[], options?: { replace?: boolean }) => {
    const next = toParam(ids)
    setTargetIds(ids)
    setShownParam(next)
    setSearchParams({ indices: next }, options)
  }

  const goToIndex = (index: number) => {
    const id = data.items[index]?.screenshotId
    if (!id) return
    writeLastViewedIndex(data.analysis?.gameId ?? '', index)
    showTargets([id])
  }

  const requestIndex = (index: number) => {
    const id = data.items[index]?.screenshotId
    if (!id) return
    if (isDirty) setPendingTarget({ ids: [id], fromUrl: false })
    else goToIndex(index)
  }

  const applyTarget = ({ ids, fromUrl }: PendingTarget) => {
    if (fromUrl) setTargetIds(ids)
    else if (ids?.length) goToIndex(viewsById.get(ids[0])?.index ?? -1)
  }

  // Puts the URL back to the screenshots still shown.
  const keepTargets = (target?: PendingTarget) => {
    if (target?.fromUrl && targetIds) showTargets(targetIds, { replace: true })
  }

  const takeOut = (screenshotId: string) => {
    const remaining = (targetIds ?? []).filter((id) => id !== screenshotId && viewsById.has(id))
    if (remaining.length) showTargets(remaining)
  }

  const back = () => navigate(getAnalysisScreenshotsPath(analysisId))

  if (data.error) return <Typography color="error.main">{t(`internal-analysts:analysis_screenshots_load_failed_${data.error}`)}</Typography>
  if (data.isLoading || !data.analysis || !data.game || targetIds === undefined) return <Skeleton variant="rectangular" height={480} />
  if (!views.length) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Typography color="error.main">{t('internal-analysts:analysis_screenshots_edit_not_found')}</Typography>
        <Button onClick={back}>{t('internal-analysts:analysis_screenshots_back_to_list')}</Button>
      </Stack>
    )
  }

  const index = views[0].index
  const title = single
    ? t('internal-analysts:analysis_screenshots_edit_title', { position: index + 1, count: data.items.length })
    : t('internal-analysts:analysis_screenshots_edit_multiple_title', { count: views.length })

  return (
    <Box pb={8}>
      <ScreenshotsHeader
        title={title}
        analysis={data.analysis}
        game={data.game}
        backLabel={t('internal-analysts:analysis_screenshots_back_to_list')}
        onBack={back}
        actions={
          <>
            <Button variant="outlined" startIcon={<Add />} disabled={isBusy} onClick={() => setIsDialogOpen(true)}>
              {t('internal-analysts:add_features_title')}
            </Button>
            <Button variant="contained" startIcon={<Save />} disabled={isBusy || !isDirty} onClick={save}>
              {t('internal-common:save')}
            </Button>
          </>
        }
      />
      <WriteNotice notice={notice} onClose={() => setNotice(undefined)} />

      <Grid container spacing={3} wrap="wrap">
        <Grid item xs={12} md={7}>
          {single ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title={index === 0 ? t('internal-analysts:analysis_screenshots_no_previous') : t('internal-analysts:analysis_screenshots_previous')}>
                <span>
                  <IconButton
                    aria-label={t('internal-analysts:analysis_screenshots_previous')}
                    disabled={isBusy || index === 0}
                    onClick={() => requestIndex(index - 1)}
                  >
                    <ChevronLeft />
                  </IconButton>
                </span>
              </Tooltip>
              <Link href={views[0].item.url} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, display: 'block' }}>
                <Box
                  component="img"
                  src={getThumbnailUrl(views[0].item.url)}
                  alt={title}
                  sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', bgcolor: 'grey.900', display: 'block', borderRadius: borderRadiusDefault }}
                />
              </Link>
              <Tooltip
                title={index === data.items.length - 1 ? t('internal-analysts:analysis_screenshots_no_next') : t('internal-analysts:analysis_screenshots_next')}
              >
                <span>
                  <IconButton
                    aria-label={t('internal-analysts:analysis_screenshots_next')}
                    disabled={isBusy || index === data.items.length - 1}
                    onClick={() => requestIndex(index + 1)}
                  >
                    <ChevronRight />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : (
            <Grid container spacing={1} wrap="wrap">
              {views.map((view) => (
                <Grid item xs={6} sm={4} key={view.item.screenshotId}>
                  <Box position="relative" onMouseEnter={() => setHoveredId(view.item.screenshotId)} onMouseLeave={() => setHoveredId(undefined)}>
                    <ButtonBase
                      sx={{ width: '100%', display: 'block', borderRadius: borderRadiusDefault, overflow: 'hidden' }}
                      aria-label={t('internal-analysts:analysis_screenshots_card_label', { position: view.index + 1 })}
                      onFocus={() => setHoveredId(view.item.screenshotId)}
                      onBlur={() => setHoveredId(undefined)}
                    >
                      <Box
                        component="img"
                        src={getThumbnailUrl(view.item.url)}
                        alt=""
                        sx={{ width: '100%', height: 200, objectFit: 'contain', bgcolor: 'grey.900', display: 'block' }}
                      />
                    </ButtonBase>
                    <Typography variant="caption" fontWeight="bold" sx={{ position: 'absolute', top: 4, left: 8, color: 'common.white' }}>
                      {view.index + 1}.
                    </Typography>
                    {views.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={t('internal-analysts:analysis_screenshots_take_out', { position: view.index + 1 })}
                        onClick={() => takeOut(view.item.screenshotId)}
                        disabled={isBusy}
                        sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              {!single && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {t('internal-analysts:analysis_screenshots_hover_help')}
                </Typography>
              )}
              <ScreenshotFeatureList
                features={featureRows}
                concepts={conceptRows}
                unknownConceptIds={stored.unknownConceptIds}
                total={views.length}
                disabled={isBusy}
                onRemoveFeature={(row) => askRemove({ kind: 'feature', row })}
                onRemoveConcept={(row) => askRemove({ kind: 'concept', row })}
              />
              {isDirty && (
                <Typography variant="body2" color="warning.main" mt={2}>
                  {t('internal-analysts:analysis_screenshots_pending_help')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <AddFeaturesDialog
        open={isDialogOpen}
        features={data.answerFeatures}
        addedFeatureLegacyIds={addedFeatureLegacyIds}
        addedConceptIds={addedConceptIds}
        onClose={() => setIsDialogOpen(false)}
        onAdd={handleAdd}
      />
      <ConfirmDialog
        open={!!removeRequest}
        confirmDialogData={removeRequest}
        onClose={(confirmed) => {
          setRemoveRequest(undefined)
          if (confirmed?.data?.kind === 'feature') removeStoredFeature(confirmed.data.row)
          if (confirmed?.data?.kind === 'concept') removeStoredConcept(confirmed.data.row)
        }}
      />
      <UnsavedChangesDialog
        open={guard.isBlocked || pendingTarget !== undefined}
        onCancel={() => {
          guard.cancel()
          keepTargets(pendingTarget)
          setPendingTarget(undefined)
        }}
        onDiscard={() => {
          discard()
          if (pendingTarget) {
            applyTarget(pendingTarget)
            setPendingTarget(undefined)
          } else guard.proceed()
        }}
        onSave={async () => {
          const target = pendingTarget
          setPendingTarget(undefined)
          const saved = await save()
          if (!saved) {
            guard.cancel()
            keepTargets(target)
            return
          }
          if (target) applyTarget(target)
          else guard.proceed()
        }}
      />
    </Box>
  )
}
